import { getOwner } from '@ember/application';
import { concat } from '@ember/helper';
import { action, set } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import SettingsCompetitorsCompetitorList from 'lumen/components/settings/competitors/competitor-list';
import SettingsCompetitorsCompetitorMap from 'lumen/components/settings/competitors/competitor-map';
import getIndexForCompetitorId from 'lumen/utils/competitors/get-index-for-competitor-id';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';

import styles from './competitor-panel.module.css';

const COMPSET_TAB_ID = 'compset';
const RMS_TAB_ID = 'rms';

interface SettingsCompetitorsCompetitorPanelSignature {
  Args: {
    activeTab: unknown;
    hasNoRadiusLimit: unknown;
    hotel: unknown;
    isBenchmark?: boolean;
    locale: unknown;
    onCancel: () => void;
    onCompetitorsChange: () => void;
    onSuccess: unknown;
    showSecondaryCompset?: boolean;
  };
}

export default class SettingsCompetitorsCompetitorPanel extends Component<SettingsCompetitorsCompetitorPanelSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  @tracked activeTabId = this.args.activeTab ?? COMPSET_TAB_ID;

  @tracked compsets = trackedObject({
    compsets: null,
    isLoading: false,
    isModified: false,
    isRMS: false,
    uniqueCompetitors: null,
  });

  @tracked isRMSTabActive = false;
  @tracked mapIsAvailable = false;

  get canEditCurrentCompset(): boolean {
    if (this.isRMSTabActive) {
      return this.access.canEditRmsCompset;
    }

    return this.access.canEditAppCompset;
  }

  get isSecondaryCompsetVisible(): boolean {
    return this.args.showSecondaryCompset ?? true;
  }

  constructor(
    owner: Owner,
    args: SettingsCompetitorsCompetitorPanelSignature['Args'],
  ) {
    super(owner, args);

    args.onCancel();

    this.loadCompsets(this.activeTabId === RMS_TAB_ID);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { environment } =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      getOwner(this).resolveRegistration('config:environment');

    if (environment === 'test') {
      this.mapIsAvailable = false; // Disable maps rendering for visual tests
    } else {
      this.mapIsAvailable = isGoogleMapsAvailable();
    }
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createCompetitorMapping(compset, competitor) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { isPrimary, isSecondary, isRMS } = compset;
    const uniqueCompetitor = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hotelinfo: competitor,
      highlighted: false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isPrimary,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isSecondary,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isRMS,
    };
    return uniqueCompetitor;
  }

  // Load specific compset by tab index
  // @ts-expect-error: Incorrect type
  loadCompsets(loadRMS): void {
    if (!this.args.hotel) {
      return;
    }

    if (loadRMS) {
      this.loadRMSCompset();
    } else {
      this.loadPrimaryAndSecondaryCompset();
    }
  }

  loadPrimaryAndSecondaryCompset(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const filteredCompsets = this.args.hotel?.compsets.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (compset) => compset.isPrimary || compset.isSecondary,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const uniqueCompetitors = this.mapUniqueCompetitors(filteredCompsets);

    this.compsets = trackedObject({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      compsets: filteredCompsets,
      isLoading: false,
      isModified: false,
      isRMS: false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      uniqueCompetitors,
    });

    this.args.onCancel();
    this.isRMSTabActive = false;
  }

  loadRMSCompset(): void {
    let filteredCompsets = [];
    let uniqueCompetitors = [];

    if (this.access.hasRmsCompset) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      filteredCompsets = this.args.hotel?.compsets.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (compset) => compset.isRMS,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      uniqueCompetitors = this.mapUniqueCompetitors(filteredCompsets);
    }

    this.compsets = trackedObject({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      compsets: filteredCompsets,
      isLoading: false,
      isModified: false,
      isRMS: true,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      uniqueCompetitors,
    });

    this.args.onCancel();
    this.isRMSTabActive = true;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mapUniqueCompetitors(compsets) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!compsets.length) {
      return [];
    }
    // Map primary or RMS compset
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const primaryCompset = compsets.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (compset) => compset.isPrimary || compset.isRMS,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    )[0];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    let uniqueCompetitors = primaryCompset.competitors.map((competitor) =>
      this.createCompetitorMapping(primaryCompset, competitor),
    );
    // First compset is no longer needed
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const otherCompsets = compsets.filter((compset) => compset.isSecondary);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (otherCompsets.length) {
      // Map secondary compset
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      otherCompsets.forEach((compset) => {
        // @ts-expect-error: Incorrect type
        let otherUniqueCompetitors = [];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        compset.competitors.forEach((competitor) => {
          const competitorIndex = getIndexForCompetitorId(
            uniqueCompetitors,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            competitor.id,
          );
          if (competitorIndex === -1) {
            const uniqueCompetitor = this.createCompetitorMapping(
              compset,
              competitor,
            );
            otherUniqueCompetitors.push(uniqueCompetitor);
          } else {
            // Insert all currently mapped competitors of the second compset before the one that appears in both compsets
            // Can only be secondary compset (Primary or RMS will already be handled by now)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            set(uniqueCompetitors[competitorIndex], 'isSecondary', true);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const startOfUniqueCompetitors = uniqueCompetitors.slice(
              0,
              competitorIndex,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const endOfUniqueCompetitors =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              uniqueCompetitors.slice(competitorIndex);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            uniqueCompetitors = startOfUniqueCompetitors
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              .concat([...otherUniqueCompetitors])
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .concat(endOfUniqueCompetitors);
            // Once inserted, we don't need to keep track of the competitors anymore
            otherUniqueCompetitors = [];
          }
        });
        // If there are still mapped competitors left, simply add them at the end of the array
        if (otherUniqueCompetitors.length) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          uniqueCompetitors = uniqueCompetitors.concat(otherUniqueCompetitors);
        }
        otherUniqueCompetitors = [];
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return uniqueCompetitors;
  }

  // Load actively selected compset or provide optional index for desired compset
  @action
  async loadSelectedCompsets(loadRMS = false): Promise<void> {
    if (this.compsets.isModified) {
      const isConfirmed = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.competitors.warning.discard'),
      });

      if (isConfirmed) {
        this.loadCompsets(loadRMS);
      }
    } else {
      // No need to throw a modal when there have been no changes
      this.loadCompsets(loadRMS);
    }
  }

  @action
  onCancel(): void {
    this.compsets.isModified = false;

    this.args.onCancel();
  }

  @action
  onCompetitorsChange(): void {
    this.compsets.isModified = true;

    this.args.onCompetitorsChange();
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.compsets.isLoading = isLoading;
  }

  <template>
    <div class={{concat styles.ota-card " " "grid h-full w-full grid-cols-2"}}>
      <SettingsCompetitorsCompetitorList
        @canEditCurrentCompset={{this.canEditCurrentCompset}}
        @compsets={{this.compsets}}
        {{! @glint-expect-error: Incorrect type }}
        @hasNoRadiusLimit={{@hasNoRadiusLimit}}
        @hasRmsIntegration={{this.access.hasRmsCompset}}
        @hotel={{@hotel}}
        @isBenchmark={{@isBenchmark}}
        @isLoading={{this.compsets.isLoading}}
        @isRMSTabActive={{eq this.activeTabId RMS_TAB_ID}}
        @isSecondaryCompsetVisible={{this.isSecondaryCompsetVisible}}
        @loadSelectedCompset={{this.loadSelectedCompsets}}
        @locale={{@locale}}
        @onCancel={{this.onCancel}}
        @onCompetitorsChange={{this.onCompetitorsChange}}
        @onSuccess={{@onSuccess}}
        @setIsLoading={{this.setIsLoading}}
      />
      {{#if this.mapIsAvailable}}
        {{! @glint-expect-error: Incorrect type }}
        <SettingsCompetitorsCompetitorMap
          @compsets={{this.compsets}}
          @hotel={{@hotel}}
        />
      {{else}}
        <div
          class={{concat
            styles.not-available
            " "
            "flex h-full items-center justify-center"
          }}
        >
          <p class="text-300--regular">
            {{t "settings.competitors.maps_unavailable"}}
          </p>
        </div>
      {{/if}}
    </div>
  </template>
}
